import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Storefront, Lock } from "@phosphor-icons/react";
import { acceptPendingInvite, persistInviteToken } from "@/lib/invites";
import { useGoogleSignInEnabled } from "@/lib/google_auth_enabled";
import { useSignupStatus } from "@/lib/signup_status";

export default function Signup() {
  const { signup, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const presetEmail = (params.get("email") || "").replace(/ /g, "+");
  const [f, setF] = useState({ name: "", email: presetEmail, password: "", business_name: "", venue_name: "Main Venue" });
  const [loading, setLoading] = useState(false);
  const googleEnabled = useGoogleSignInEnabled();
  const signupStatus = useSignupStatus();
  // If new-tenant signup is disabled AND the visitor has no invitation
  // in the URL, render the closed-signups screen instead of the form.
  const isInvited = !!inviteToken;
  const signupsClosed = !signupStatus.enabled && !isInvited;
  // iter_219 — Marketing pricing is no longer fetched from a public
  // endpoint. The Subscribe flow lives in Settings > Subscription
  // and reads pricing from the versioned `subscription_plans`
  // collection at runtime.

  const onChange = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup({ ...f, email: f.email.trim().toLowerCase() });
      try {
        const accepted = await acceptPendingInvite();
        if (accepted) { await refresh(); toast.success("Invitation accepted — joined the team"); }
        else toast.success("Account created — 14 day free trial started");
      } catch (e) {
        toast.error(e?.detail || "Invite could not be accepted");
      }
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  if (signupsClosed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--sand-50)] p-6"
           data-testid="signup-closed-page">
        <div className="max-w-md w-full text-center rounded-2xl border border-[rgba(28,25,23,0.08)] bg-white p-8">
          <div className="w-20 h-20 rounded-full bg-[var(--sand-100)] flex items-center justify-center mx-auto mb-4">
            <Lock size={38} weight="fill" className="text-[var(--terracotta)]" />
          </div>
          <h1 className="font-heading text-2xl font-semibold mb-2">Signups are closed</h1>
          <p className="text-sm text-[var(--ink-500)] mb-6">
            {signupStatus.message ||
             "New business signup is disabled. Ask an existing owner or manager to invite you from within the POS."}
          </p>
          <Link to="/login"
                data-testid="signup-closed-login-link"
                className="inline-flex h-10 px-6 rounded-md bg-[var(--ink-900)] text-[var(--sand-50)] text-sm font-medium items-center justify-center">
            Go to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--sand-50)] p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 rounded-md bg-[var(--terracotta)] flex items-center justify-center">
            <Storefront size={20} color="#fff" weight="fill" />
          </div>
          <div className="font-heading font-semibold text-lg">Margin Mate One POS</div>
        </div>
        <div className="bg-white border border-[rgba(28,25,23,0.08)] rounded-2xl p-8">
          <div className="label-eyebrow mb-2">Start your 14-day free trial</div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight mb-1">Create your venue</h1>
          <p className="text-sm text-[var(--ink-500)] mb-6" data-testid="signup-price-label">14-day free trial. Cancel anytime.</p>
          {inviteToken && (
            <div className="mb-5 rounded-md border border-[var(--moss-bg)] bg-[var(--moss-bg)]/40 px-3 py-2.5 text-xs text-[var(--ink-700)]" data-testid="signup-invite-banner">
              You're accepting a team invitation. Create your account to join.
            </div>
          )}
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium mb-1.5">Your name</label>
              <input required data-testid="signup-name" value={f.name} onChange={onChange("name")}
                className="w-full h-11 px-3 rounded-md border border-[rgba(28,25,23,0.12)] focus:border-[var(--terracotta)] focus:ring-2 focus:ring-[var(--terracotta)]/20 outline-none bg-white"/>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Email</label>
              <input required type="email" data-testid="signup-email" value={f.email} onChange={onChange("email")}
                className="w-full h-11 px-3 rounded-md border border-[rgba(28,25,23,0.12)] focus:border-[var(--terracotta)] focus:ring-2 focus:ring-[var(--terracotta)]/20 outline-none bg-white"/>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Password</label>
              <input required type="password" minLength={6} data-testid="signup-password" value={f.password} onChange={onChange("password")}
                className="w-full h-11 px-3 rounded-md border border-[rgba(28,25,23,0.12)] focus:border-[var(--terracotta)] focus:ring-2 focus:ring-[var(--terracotta)]/20 outline-none bg-white"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1.5">Business name</label>
                <input required data-testid="signup-business" value={f.business_name} onChange={onChange("business_name")}
                  className="w-full h-11 px-3 rounded-md border border-[rgba(28,25,23,0.12)] focus:border-[var(--terracotta)] focus:ring-2 focus:ring-[var(--terracotta)]/20 outline-none bg-white"/>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5">Venue name</label>
                <input required data-testid="signup-venue" value={f.venue_name} onChange={onChange("venue_name")}
                  className="w-full h-11 px-3 rounded-md border border-[rgba(28,25,23,0.12)] focus:border-[var(--terracotta)] focus:ring-2 focus:ring-[var(--terracotta)]/20 outline-none bg-white"/>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="signup-submit"
              className="w-full h-11 rounded-md bg-[var(--terracotta)] hover:bg-[var(--terracotta-dark)] active:bg-[var(--terracotta-darker)] active:scale-[0.99] text-white font-medium transition-all duration-200 disabled:opacity-60 mt-2"
            >
              {loading ? "Creating…" : "Start free trial"}
            </button>
          </form>
          {googleEnabled && (
          <>
          <div className="flex items-center gap-3 my-4 text-[10px] uppercase tracking-[0.18em] text-[var(--ink-500)]">
            <span className="flex-1 h-px bg-[rgba(28,25,23,0.08)]"></span> or <span className="flex-1 h-px bg-[rgba(28,25,23,0.08)]"></span>
          </div>
          <button
            type="button"
            data-testid="signup-google"
            onClick={() => {
              if (inviteToken) persistInviteToken(inviteToken);
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/auth/google/callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full h-11 rounded-md border border-[rgba(28,25,23,0.12)] bg-white hover:bg-[var(--sand-100)] text-sm font-medium flex items-center justify-center gap-2 active:scale-[0.99]"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC04" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18A10.99 10.99 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </button>
          </>
          )}
        </div>
        <p className="text-sm text-[var(--ink-500)] mt-6 text-center">
          Already have an account?{" "}
          <Link to="/login" data-testid="goto-login" className="text-[var(--terracotta)] hover:underline font-medium">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
